/**
 * ニュースページのページネーション機能
 * 1ページに4件の記事を表示し、複数ページがある場合はページ番号とNext/Backボタンを表示する
 * 
 * @example
 * // HTMLに以下の構造が必要
 * // <div class="cnt__page">
 * //   <div class="cnt__pageBack"><p>back</p></div>
 * //   <div class="cnt__pageNum"></div>
 * //   <div class="cnt__pageNext"><p>next</p></div>
 * // </div>
 * 
 * // 使用方法
 * import { initNewsPagePagination } from './module/page-newsPage';
 * initNewsPagePagination();
 */

interface PaginationData {
    max_pages: number;
    current_page: number;
    posts_per_page: number;
    category: string;
}

/**
 * ページネーションを初期化する関数
 */
export const initNewsPagePagination = (): void => {
    const paginationContainer = document.querySelector('.cnt__page');
    if (!paginationContainer) return;

    const backBtn = paginationContainer.querySelector('.cnt__pageBack');
    const nextBtn = paginationContainer.querySelector('.cnt__pageNext');
    const numContainer = paginationContainer.querySelector('.cnt__pageNum');

    if (!backBtn || !nextBtn || !numContainer) return;

    // PHPから渡されたデータを取得
    const data = (window as any).paginationData as PaginationData;
    if (!data) return;

    const { max_pages, current_page, category } = data;

    console.log('Initializing pagination:', data);

    // ページ番号を生成（1ページでも「1」を表示）
    renderPageNumbers(numContainer, current_page, max_pages, category);

    // Backボタンの制御
    if (current_page <= 1) {
        backBtn.classList.add('is-disabled');
    } else {
        backBtn.classList.remove('is-disabled');
        backBtn.addEventListener('click', () => {
            navigateToPage(current_page - 1, category);
        });
    }

    // Nextボタンの制御
    if (current_page >= max_pages) {
        nextBtn.classList.add('is-disabled');
    } else {
        nextBtn.classList.remove('is-disabled');
        nextBtn.addEventListener('click', () => {
            navigateToPage(current_page + 1, category);
        });
    }
};

/**
 * ページ番号を描画する関数
 * @param container - ページ番号を表示するコンテナ要素
 * @param currentPage - 現在のページ番号
 * @param maxPages - 最大ページ数
 * @param category - カテゴリースラッグ
 */
const renderPageNumbers = (
    container: Element,
    currentPage: number,
    maxPages: number,
    category: string
): void => {
    container.innerHTML = '';

    for (let i = 1; i <= maxPages; i++) {
        const pageNum = document.createElement('div');
        pageNum.classList.add('cnt__pageNumItem');
        
        // <p>タグを作成してボタンの中に追加
        const p = document.createElement('p');
        p.textContent = i.toString();
        pageNum.appendChild(p);

        if (i === currentPage) {
            pageNum.classList.add('is-current');
        }

        pageNum.addEventListener('click', () => {
            navigateToPage(i, category);
        });

        container.appendChild(pageNum);
    }
};

/**
 * 指定したページに遷移する関数
 * @param page - 遷移先のページ番号
 * @param category - カテゴリースラッグ
 */
const navigateToPage = (page: number, category: string): void => {
    let url = '';
    
    // 現在のURLから基準URLを取得
    const currentPath = window.location.pathname;
    
    if (category) {
        // カテゴリーページの場合
        url = `/news/category/${category}/`;
    } else {
        // 通常のニュースアーカイブの場合
        url = '/news/';
    }
    
    if (page > 1) {
        url += `page/${page}/`;
    }
    
    console.log('Navigating to:', url);
    window.location.href = url;
};

// DOM読み込み完了後に自動実行
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNewsPagePagination);
} else {
    initNewsPagePagination();
}
